import { CalendarDate, DateValue, toCalendarDateTime } from '@internationalized/date';
import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon, ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { range } from 'lodash/fp';
import { Settings } from 'luxon';
import { Ref, useRef } from 'react';
import {
  DateRange,
  RangeCalendarProps as RangeCalendarBaseProps,
  useDateFormatter,
  useLocale,
  useRangeCalendar,
  VisuallyHidden,
} from 'react-aria';
import { RangeCalendarState, useRangeCalendarState } from 'react-stately';
import styled from 'styled-components';

import Card from 'design-system/components/Card';
import {
  CalendarButton,
  CalendarContainer,
  CalendarHeader,
  CalendarHeading,
} from 'design-system/components/DatePicker/Calendar/CalendarBase';
import CalendarGrid from 'design-system/components/DatePicker/Calendar/CalendarGrid';
import RangeCalendarPresets from 'design-system/components/DatePicker/Calendar/RangeCalendarPresets';
import { createCalendar } from 'design-system/components/DatePicker/common';

const RangeCalendarHeader = styled(CalendarHeader)`
  gap: ${Spacing[5]};
`;

const CalendarHeaderMonth = styled.div`
  display: grid;
  grid-template-columns: minmax(auto, 1fr) auto minmax(auto, 1fr);
  grid-template-areas: 'prev title next';
  width: 266px;
  align-items: center;
`;

const CalendarGrids = styled.div`
  display: flex;
  gap: ${Spacing[5]};
`;

const PrevButton = styled(CalendarButton)`
  grid-area: prev;
  justify-self: start;
  border-radius: ${BorderRadius.Sm};
`;

const NextButton = styled(CalendarButton)`
  grid-area: next;
  justify-self: end;
  border-radius: ${BorderRadius.Sm};
`;

const RangeCalendarHeading = styled(CalendarHeading)`
  grid-area: title;
`;

const RangeCalendarContainer = styled.div`
  display: flex;
`;

const PresetsContainer = styled(Card.Body)`
  border-right: 1px solid ${Color.BorderHairline};
`;

interface RangeCalendarProps extends RangeCalendarBaseProps<DateValue> {
  minDate?: RangeCalendarBaseProps<DateValue>['minValue'];
  maxDate?: RangeCalendarBaseProps<DateValue>['maxValue'];
  calendarRef?: Ref<HTMLDivElement>;
  hidePresets?: boolean;
  onSelectDate?: (d: CalendarDate) => void;
  setDateMode: (mode: 'start' | 'end' | null) => void;
  /**
   * From setValue of DateRangePicker
   */
  setValue: (value: DateRange | null) => void;
  granularity: 'day' | 'hour' | 'minute' | 'second';
}

const RangeCalendar = ({
  calendarRef,
  onSelectDate,
  minDate,
  maxDate,
  hidePresets,
  setDateMode,
  setValue,
  granularity,
  ...props
}: RangeCalendarProps) => {
  const visibleMonths = 2;
  const { locale } = useLocale();
  // Must gather the TZ from the user's settings.
  const timezoneName = Settings.defaultZone.name;
  const rangeCalendarState = useRangeCalendarState({
    ...props,
    minValue: minDate,
    maxValue: maxDate,
    visibleDuration: {
      months: visibleMonths,
    },
    locale,
    createCalendar,
  });

  const state: RangeCalendarState = {
    ...rangeCalendarState,
    setValue: (value) => {
      if (!value || granularity === 'day') {
        rangeCalendarState.setValue(value);
        return;
      }

      setValue({
        start: toCalendarDateTime(value.start),
        end: toCalendarDateTime(value.end),
      });
    },
    selectDate: (date) => {
      if (onSelectDate) {
        onSelectDate(date);
        rangeCalendarState.setAnchorDate(null);
      } else {
        rangeCalendarState.selectDate(date);
      }
    },
  };

  const ref = useRef();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(props, state, ref);

  const monthDateFormatter = useDateFormatter({
    month: 'long',
    year: 'numeric',
    timeZone: timezoneName,
  });

  const monthHeaders = range(0, visibleMonths).map((i) => {
    return (
      <CalendarHeaderMonth key={`calendar-month-${i}`}>
        {i === 0 && (
          <PrevButton {...prevButtonProps}>
            <ArrowLeftIcon />
          </PrevButton>
        )}
        <RangeCalendarHeading aria-hidden>
          {monthDateFormatter.format(
            state.visibleRange.start.add({ months: i }).toDate(timezoneName),
          )}
        </RangeCalendarHeading>
        {i === visibleMonths - 1 && (
          <NextButton {...nextButtonProps}>
            <ArrowRightIcon />
          </NextButton>
        )}
      </CalendarHeaderMonth>
    );
  });

  const monthGrids = range(0, visibleMonths).map((i) => {
    return <CalendarGrid key={`calendar-grid-${i}`} state={state} offset={{ months: i }} />;
  });

  return (
    <RangeCalendarContainer ref={calendarRef}>
      {!hidePresets && (
        <PresetsContainer padding={Spacing[3]}>
          <RangeCalendarPresets state={state} setDateMode={setDateMode} />
        </PresetsContainer>
      )}
      <Card.Body padding={Spacing[3]}>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <CalendarContainer {...calendarProps} ref={ref}>
          {/* Calendar header is separate from grid like this to facilitate a11y. */}
          <RangeCalendarHeader>
            <VisuallyHidden>${calendarProps['aria-label']}</VisuallyHidden>
            {monthHeaders}
          </RangeCalendarHeader>
          <CalendarGrids>{monthGrids}</CalendarGrids>
        </CalendarContainer>
      </Card.Body>
    </RangeCalendarContainer>
  );
};

export default RangeCalendar;
